import type {HTMLAttributes} from 'react'

import {useBoardDataContext} from '../provider/contexts'
import type {BoardRow, RowKey} from '../types'

export type UseBoardRowResult = {
  /** The row at this key, or `undefined` when the board holds no such row. */
  row: BoardRow | undefined
  rowProps: HTMLAttributes<HTMLElement>
}

/**
 * One swimlane's frame and its header. A board with no swimlane has exactly one row, so
 * this hook runs for every board and nothing anywhere asks whether the board is
 * swimlaned. Like a column, a row is not a drop target.
 *
 * **The swimlane is introduced by its heading and nothing else.** It carries no role or
 * name of its own: a named group around a swimlane announces it a second time around the
 * heading that already introduces it. The cells inside are still named after the
 * swimlane they are in.
 *
 * A swimlane is not a place the board's focus lands. It is not interactive, the cell a
 * reader arrives in is already named after the swimlane it is in, and a stop there would
 * be one more position a caller has to draw or lose the arrow keys to.
 */
export function useBoardRow(rowKey: RowKey): UseBoardRowResult {
  const {rowsByKey} = useBoardDataContext()
  const row = rowsByKey.get(rowKey)

  return {
    row,
    // Empty, and kept so a caller assembling its own row has one place to spread from.
    rowProps: {},
  }
}
